import React from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { css, keyframes } from '@emotion/css';
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  LinearProgress,
  MenuItem,
  TablePagination,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { OrderStatusPalette } from '@hopdrive/sdk-theme';
import { MOBILITY_RUN_TILE_STATUSES } from '@hopdrive/feature-order-index';
import { MdAccessTime, MdDirectionsCar, MdPerson, MdPlace, MdSearch } from 'react-icons/md';

import DefaultFallback from '@components/Fallbacks/DefaultFallback';
import Spacer from '@components/Utils/Spacer';

import type { CountWindow } from '@features/products/counts/productCounts';
import { useShellTabActions } from '@features/products/shell/shellTabActions';
import { Mobility_Runs_Bool_Exp, Mobility_Runs_Order_By } from '@gql/base-types';
import { useMobileServiceCounts } from '../counts/useMobileServiceCounts';
import { GetServiceRunsQuery, useGetServiceRunsQuery } from '../queries/getServiceRuns.query.generated';
import { useRooftop, useUser } from '@store';
import RunStatusChip from '@components/Badges/RunStatusChip';

type Run = GetServiceRunsQuery['mobility_runs'][number];

// ── Status config ──────────────────────────────────────────────── //

type MetricKey = 'all' | 'active' | 'scheduled' | 'completed' | 'cancelled';

interface MetricCard {
  key: MetricKey;
  label: string;
  statuses: string[];
  /** The order status bucket the tile is colored by; the All tile has none. */
  bucket?: keyof OrderStatusPalette;
}

const METRICS: MetricCard[] = [
  { key: 'all', label: 'All', statuses: [] },
  { key: 'active', label: 'Active', statuses: MOBILITY_RUN_TILE_STATUSES.active, bucket: 'active' },
  { key: 'scheduled', label: 'Scheduled', statuses: MOBILITY_RUN_TILE_STATUSES.upcoming, bucket: 'upcoming' },
  { key: 'completed', label: 'Completed', statuses: MOBILITY_RUN_TILE_STATUSES.completed, bucket: 'completed' },
  { key: 'cancelled', label: 'Canceled', statuses: MOBILITY_RUN_TILE_STATUSES.cancelled, bucket: 'cancelled' },
];

// ── Date range presets ──────────────────────────────────────────── //

type DateRange = 'today' | 'yesterday' | 'this_week' | 'last_week' | 'this_month' | 'last_month' | 'all';

const DATE_RANGES: { value: DateRange; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'all', label: 'All Time' },
];

const getDateBounds = (range: DateRange): { start: string | null; end: string | null } => {
  const now = dayjs();
  switch (range) {
    case 'today':
      return { start: now.startOf('day').toISOString(), end: now.endOf('day').toISOString() };
    case 'yesterday':
      return { start: now.subtract(1, 'day').startOf('day').toISOString(), end: now.subtract(1, 'day').endOf('day').toISOString() };
    case 'this_week':
      return { start: now.startOf('week').toISOString(), end: now.endOf('week').toISOString() };
    case 'last_week':
      return { start: now.subtract(1, 'week').startOf('week').toISOString(), end: now.subtract(1, 'week').endOf('week').toISOString() };
    case 'this_month':
      return { start: now.startOf('month').toISOString(), end: now.endOf('month').toISOString() };
    case 'last_month':
      return { start: now.subtract(1, 'month').startOf('month').toISOString(), end: now.subtract(1, 'month').endOf('month').toISOString() };
    case 'all':
    default:
      return { start: null, end: null };
  }
};

/** All time is no window. */
const toCountWindow = (range: DateRange): CountWindow => {
  const { start, end } = getDateBounds(range);
  return start && end ? { startIso: start, endIso: end } : null;
};

// ── Helpers ──────────────────────────────────────────────── //

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const formatDeparture = (iso?: string | null) => {
  if (!iso) return null;
  return dayjs(iso).format('M/D h:mm A');
};

const formatTotalMinutes = (run: Run) => {
  const total = run.stops.reduce((sum, stop) => {
    return sum + (stop.service_stop_detail[0]?.estimated_service_minutes ?? 0);
  }, 0);
  if (total === 0) return null;
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

// ── Component ──────────────────────────────────────────────── //

/**
 * The Runs tab of the Mobile Service shell. The layout owns the frame, the tab strip, the state
 * banner, and the header: New Run is the product's creation method there, and this tab
 * contributes its refresh as the header's sync.
 */
const RunsTab: React.FC = () => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const user = useUser();
  const rooftop = useRooftop();
  const customerId = rooftop?.id || user?.profile?.customer_id || 0;

  // Filter state
  const [metric, setMetric] = React.useState<MetricKey>('all');
  const [dateRange, setDateRange] = React.useState<DateRange>('all');
  const [search, setSearch] = React.useState('');

  // Pagination
  const [offset, setOffset] = React.useState(0);
  const [limit, setLimit] = React.useState(50);

  const orderBy = React.useMemo<Mobility_Runs_Order_By[]>(() => {
    return [{ created_at: 'desc_nulls_last' as any }, { id: 'desc_nulls_last' as any }];
  }, []);

  // Build where clause from filters
  const additionalFilters = React.useMemo<Mobility_Runs_Bool_Exp[]>(() => {
    const filters: Mobility_Runs_Bool_Exp[] = [];

    // Status filter
    const metricCfg = METRICS.find(m => m.key === metric);
    if (metricCfg && metricCfg.statuses.length > 0) {
      filters.push({ status: { _in: metricCfg.statuses } });
    }

    // Date filter
    const { start, end } = getDateBounds(dateRange);
    if (start && end) {
      filters.push({ departure_time: { _gte: start, _lte: end } });
    }

    // Search filter
    if (search.trim()) {
      const term = `%${search.trim()}%`;
      filters.push({
        _or: [
          { driver: { user: { display_name: { _ilike: term } } } },
          { stops: { service_stop_detail: { customer_name: { _ilike: term } } } },
        ],
      } as any);
    }

    return filters;
  }, [metric, dateRange, search]);

  const { data, loading, error, refetch } = useGetServiceRunsQuery({
    variables: { customerId, limit, offset, orderBy, additionalFilters },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !customerId,
  });

  const runs = data?.mobility_runs ?? [];
  const totalCount = data?.mobility_runs_aggregate?.aggregate?.count ?? 0;

  // Server counts over the tab's range, whatever tile is selected.
  const countWindow = React.useMemo(() => toCountWindow(dateRange), [dateRange]);
  const { counts, refetch: refetchCounts } = useMobileServiceCounts({ customerIds: [customerId], window: countWindow });
  const metricCounts = React.useMemo<Record<MetricKey, number>>(() => {
    const buckets = counts?.buckets;
    return {
      all: buckets?.total ?? 0,
      active: buckets?.active ?? 0,
      scheduled: buckets?.upcoming ?? 0,
      completed: buckets?.completed ?? 0,
      cancelled: buckets?.cancelled ?? 0,
    };
  }, [counts]);

  const refetchAll = React.useCallback(() => {
    void refetch();
    refetchCounts();
  }, [refetch, refetchCounts]);
  const tabActions = React.useMemo(() => ({ sync: refetchAll }), [refetchAll]);
  useShellTabActions(tabActions);

  // Reset pagination when filters change
  React.useEffect(() => { setOffset(0); }, [metric, dateRange, search]);

  const [loadingVisible, setLoadingVisible] = React.useState(false);
  React.useEffect(() => {
    if (loading) setLoadingVisible(true);
    else setTimeout(() => setLoadingVisible(false), 250);
  }, [loading]);

  return (
    <>
      {/* ── Metric Cards ── */}
      <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {METRICS.map(m => {
          const isSelected = metric === m.key;
          const count = metricCounts[m.key];
          const tileColor = m.bucket ? theme.palette.orderStatus[m.bucket] : theme.palette.text.secondary;
          const tileTint = alpha(tileColor, 0.05);
          return (
            <Card
              key={m.key}
              elevation={0}
              onClick={() => setMetric(m.key)}
              sx={{
                cursor: 'pointer',
                px: 2,
                py: 1.25,
                minWidth: 100,
                flex: '1 1 0',
                border: '1px solid',
                borderColor: isSelected ? tileColor : 'divider',
                bgcolor: isSelected ? tileTint : 'background.paper',
                transition: 'all 0.15s',
                '&:hover': { borderColor: tileColor, bgcolor: tileTint },
              }}
            >
              <Typography variant='h6' sx={{ fontWeight: 700, color: isSelected ? tileColor : 'text.primary', lineHeight: 1.2 }}>
                {count}
              </Typography>
              <Typography variant='caption' sx={{ fontWeight: 600, color: isSelected ? tileColor : 'text.secondary' }}>
                {m.label}
              </Typography>
            </Card>
          );
        })}
      </Box>

      {/* ── Filter Controls ── */}
      <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <TextField
          select
          size='small'
          label='Date Range'
          value={dateRange}
          onChange={e => setDateRange(e.target.value as DateRange)}
          sx={{ minWidth: 140 }}
        >
          {DATE_RANGES.map(r => (
            <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          size='small'
          placeholder='Search by name or driver...'
          value={search}
          onChange={e => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: <Box component={MdSearch} size={18} sx={{ mr: 1, color: 'text.disabled' }} />,
            },
          }}
          sx={{ minWidth: 240 }}
        />
      </Box>

      {loadingVisible ? (
        <LinearProgress className={loading ? cls.fadeIn : cls.fadeOut} />
      ) : (
        <div className={cls.loadingPad} />
      )}

      {error ? (
        <>
          <Spacer size={2} />
          <DefaultFallback fullscreen severity='error' message='Failed to fetch runs.' />
        </>
      ) : null}

      {!loading && !error && runs.length === 0 ? (
        <>
          <Spacer size={2} />
          <DefaultFallback
            fullscreen
            severity='warning'
            message={metric !== 'all' || dateRange !== 'all' || search
              ? 'No runs match your filters.'
              : 'No service runs found. Click “New Run” to build one.'
            }
          />
        </>
      ) : null}

      {!error && runs.length > 0 ? (
        <>
          <TablePagination
            className={cls.pagination}
            component='div'
            count={totalCount}
            page={Math.floor(offset / limit)}
            onPageChange={(_e, p) => setOffset(p * limit)}
            rowsPerPage={limit}
            onRowsPerPageChange={e => { setLimit(parseInt(e.target.value, 10)); setOffset(0); }}
            rowsPerPageOptions={[25, 50, 100]}
          />

          <div className={loading ? cls.contentLoading : cls.content}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
              {runs.map(run => {
                const stopCount = run.stops_aggregate?.aggregate?.count ?? run.stops.length;
                const driverName = run.driver?.user?.display_name;
                const vehicleLabel = run.vehicle
                  ? [run.vehicle.year, run.vehicle.make, run.vehicle.model].filter(Boolean).join(' ')
                  : null;
                const estTime = formatTotalMinutes(run);
                const departure = formatDeparture(run.departure_time);

                return (
                  <Card key={run.id} elevation={1}>
                    <CardActionArea
                      component={Link}
                      to={`/mobile-service/runs/${run.id}`}
                      sx={{ px: 2.5, py: 2 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        {/* Run ID + status */}
                        <Box sx={{ flex: '1 1 140px', minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant='body2' sx={{ fontWeight: 700 }}>
                              Run #{run.id}
                            </Typography>
                            <RunStatusChip status={run.status} variant='outlined' sx={{ fontSize: 11, fontWeight: 600, height: 22 }} />
                          </Box>
                          {departure ? (
                            <Typography variant='caption' color='text.secondary'>
                              {departure}
                            </Typography>
                          ) : (
                            <Typography variant='caption' color='text.disabled'>
                              No departure set
                            </Typography>
                          )}
                        </Box>

                        {/* Technician */}
                        <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: 'secondaryAccent.main', fontSize: 11, fontWeight: 700 }}>
                              {driverName ? getInitials(driverName) : <MdPerson size={14} />}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant='caption' color='text.secondary' sx={{ display: 'block', lineHeight: 1.2 }}>
                                Technician
                              </Typography>
                              <Typography variant='body2' sx={{ fontWeight: 600 }} noWrap>
                                {driverName || <Box component='em' sx={{ fontWeight: 400, color: 'text.disabled' }}>Auto-assign</Box>}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* Vehicle */}
                        <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar variant='rounded' sx={{ width: 28, height: 28, bgcolor: 'grey.100' }}>
                              <MdDirectionsCar size={16} color={theme.palette.text.secondary} />
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant='caption' color='text.secondary' sx={{ display: 'block', lineHeight: 1.2 }}>
                                Vehicle
                              </Typography>
                              <Typography variant='body2' sx={{ fontWeight: 600 }} noWrap>
                                {vehicleLabel || <Box component='em' sx={{ fontWeight: 400, color: 'text.disabled' }}>Unassigned</Box>}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* Stops + time */}
                        <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center', flexShrink: 0 }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <MdPlace size={14} color={theme.palette.text.secondary} />
                              <Typography variant='body2' sx={{ fontWeight: 700 }}>{stopCount}</Typography>
                            </Box>
                            <Typography variant='caption' color='text.secondary'>
                              stop{stopCount !== 1 ? 's' : ''}
                            </Typography>
                          </Box>

                          {estTime ? (
                            <Box sx={{ textAlign: 'center' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <MdAccessTime size={14} color={theme.palette.text.secondary} />
                                <Typography variant='body2' sx={{ fontWeight: 700 }}>{estTime}</Typography>
                              </Box>
                              <Typography variant='caption' color='text.secondary'>est. time</Typography>
                            </Box>
                          ) : null}
                        </Box>
                      </Box>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Box>

            <TablePagination
              className={cls.pagination}
              component='div'
              count={totalCount}
              page={Math.floor(offset / limit)}
              onPageChange={(_e, p) => setOffset(p * limit)}
              rowsPerPage={limit}
              onRowsPerPageChange={e => { setLimit(parseInt(e.target.value, 10)); setOffset(0); }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </div>
        </>
      ) : null}
    </>
  );
};

const useStyles = (theme: Theme) => {
  const fadeIn = keyframes`0% { opacity: 0; } 100% { opacity: 1; }`;
  const fadeOut = keyframes`0% { opacity: 1; } 100% { opacity: 0; }`;
  const contentFadeIn = keyframes`0% { opacity: 0; } 33% { opacity: 0; } 100% { opacity: 1; }`;

  return {
    fadeIn: css`opacity: 1; animation: ${fadeIn} 0.25s ease-in-out;`,
    fadeOut: css`opacity: 0; animation: ${fadeOut} 0.25s ease-in-out;`,
    loadingPad: css`width: 100%; height: 4px;`,
    content: css`opacity: 1; transition: opacity 0.25s ease-in-out; animation: ${contentFadeIn} 0.5s ease-in-out;`,
    contentLoading: css`opacity: 0; transition: opacity 0.25s ease-in-out; user-select: none; pointer-events: none;`,
    pagination: css`border-bottom: 1px solid ${theme.palette.divider}; margin-right: 16px;`,
  };
};

export default RunsTab;
